import type { MoGroup, MoLine } from './api';
import { cellText, passes, type ColFilters } from './colFilter';

/**
 * 월정기 좌우 화면의 거르기·고르기 규칙(2026-10-02 함남열 — 칼럼 거르기 · 머리 전체 고르기 · 전체 보기).
 *
 * 화면은 그 달 목록을 **전부** 받아(차주 100명씩 끝까지) 브라우저에서 거른다. 고르기는 늘
 * **지금 걸린 검색·거르기 결과 안**에서만 산다 — 걸러서 안 보이게 된 줄은 고름에서도 빠진다.
 * 그래서 「선택등록」·「선택취소」가 보내는 번호 = 화면에 걸려 있는 줄 중 고른 것뿐이다.
 *
 * 순수 함수만 — `scripts/mo-board-check.mjs` 가 이 파일을 그대로 묶어 점검한다.
 */

export type Side = 'def' | 'month';

const won = (v: string | null) => {
  if (v === null || v === undefined || v === '' || Number(v) === 0) return '';
  return Math.round(Number(v)).toLocaleString('ko-KR');
};

/** 칼럼별 「화면에 보이는 글자」 — 거르기 값 목록과 비교가 이 글자로 한다. */
export const MO_GETTERS: Record<string, (r: MoLine) => string> = {
  owner: (r) => cellText(r.owner_name),
  no: (r) => cellText(r.result_mo_id),
  driver: (r) => cellText(r.driver_name),
  car: (r) => cellText(r.car_no),
  grp: (r) => cellText(r.grp_name),
  account: (r) => cellText(r.account_name),
  plus: (r) => cellText(r.is_plus ? won(r.amt) : ''),
  minus: (r) => cellText(r.is_plus ? '' : won(r.amt)),
  alloc: (r) => cellText(r.car_alloc_id),
  driver_id: (r) => cellText(r.driver_id),
  owner_id: (r) => cellText(r.owner_id),
  comment: (r) => cellText(r.comment),
  created: (r) => cellText(r.created_at),
  creator: (r) => cellText(r.created_name),
};

/** 고르기 번호 — 왼쪽은 정기항목 번호, 오른쪽은 그 달 월정산 번호. */
export const keyOf = (side: Side, r: MoLine): number | null =>
  (side === 'def' ? r.result_mo_def_id : r.result_mo_id);

/** 왼쪽에서 이미 넘어간(그 달에 등록된) 줄은 못 고른다. */
export const pickable = (side: Side, r: MoLine): boolean =>
  keyOf(side, r) != null && (side === 'month' || r.result_mo_id == null);

/** 거르기를 건 차주 묶음 — 줄이 하나도 안 남은 차주는 빠지고, 건수·소계는 남은 줄로 다시 센다. */
export function filterGroups(groups: MoGroup[], filters: ColFilters): MoGroup[] {
  const out: MoGroup[] = [];
  for (const g of groups) {
    const rows = g.rows.filter((r) => passes(r, filters, MO_GETTERS));
    if (rows.length === 0) continue;
    if (rows.length === g.rows.length) { out.push(g); continue; }
    // 소계는 표시용이다(금액 계산에 쓰지 않는다) — 서버 소계와 같은 방식(지급·공제 따로).
    let plus = 0; let minus = 0;
    for (const r of rows) {
      const a = Number(r.amt ?? 0);
      if (r.is_plus) plus += a; else minus += a;
    }
    out.push({ ...g, rows, n: rows.length, plus: String(plus), minus: String(minus) });
  }
  return out;
}

/** 지금 걸린 결과 안에서 고를 수 있는 번호 전부(모든 쪽 — 보이는 쪽만이 아니다). */
export function selectableIds(side: Side, groups: MoGroup[]): number[] {
  const ids: number[] = [];
  for (const g of groups) for (const r of g.rows) if (pickable(side, r)) ids.push(keyOf(side, r)!);
  return ids;
}

/** 고름을 지금 결과 안으로 줄인다(걸러서 사라진 줄은 고름에서도 뺀다). 바뀐 게 없으면 같은 Set 을 돌려준다. */
export function pruneTo(picked: Set<number>, allowed: Set<number>): Set<number> {
  let changed = false;
  const n = new Set<number>();
  for (const id of picked) { if (allowed.has(id)) n.add(id); else changed = true; }
  return changed ? n : picked;
}

/** 「선택등록」·「선택취소」가 보낼 번호 = 고른 것 ∩ 지금 결과에서 고를 수 있는 것. */
export function idsToSend(side: Side, picked: Set<number>, groups: MoGroup[]): number[] {
  return selectableIds(side, groups).filter((id) => picked.has(id));
}

/** 머리 전체 고르기 — 결과 전부를 고르거나(on) 결과 전부를 뺀다. 결과 밖 고름은 건드리지 않는다. */
export function setAll(picked: Set<number>, ids: number[], on: boolean): Set<number> {
  const n = new Set(picked);
  for (const id of ids) { if (on) n.add(id); else n.delete(id); }
  return n;
}

/** 칸 정렬(#411 홍명옥) — 오름/내림. 숫자 칸은 숫자로, 글자는 가나다로, 빈 값은 늘 맨 뒤. */
export type MoSort = { key: string; dir: 'asc' | 'desc' } | null;
const NUM_KEYS = new Set(['no', 'plus', 'minus', 'alloc', 'driver_id', 'owner_id']);
const sortVal = (key: string, r: MoLine): string | number | null => {
  if (key === 'plus') return r.is_plus && Number(r.amt) ? Number(r.amt) : null;
  if (key === 'minus') return !r.is_plus && Number(r.amt) ? Number(r.amt) : null;
  const t = MO_GETTERS[key]?.(r) ?? '';
  if (t === '') return null;
  return NUM_KEYS.has(key) ? Number(t.replace(/,/g, '')) : t;
};
function cmp(a: string | number | null, b: string | number | null, dir: 'asc' | 'desc'): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
  const d = typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b), 'ko', { numeric: true });
  return dir === 'asc' ? d : -d;
}

/**
 * 차주 묶음은 그대로 두고(소계·차주 단위 고르기) — 묶음 안 줄을 그 칸으로 정렬하고, 묶음 순서는 정렬된 첫 줄 값으로.
 * 차주 칸은 묶음 순서만 바꾼다. 같은 값이면 원래 순서(안정 정렬).
 */
export function sortGroups(groups: MoGroup[], sort: MoSort): MoGroup[] {
  if (!sort) return groups;
  const { key, dir } = sort;
  const inner = key === 'owner' ? groups
    : groups.map((g) => ({ ...g, rows: [...g.rows].sort((a, b) => cmp(sortVal(key, a), sortVal(key, b), dir)) }));
  const head = (g: MoGroup): string | number | null =>
    key === 'owner' ? (g.owner_name ?? null) : (g.rows[0] ? sortVal(key, g.rows[0]) : null);
  return [...inner].sort((a, b) => cmp(head(a), head(b), dir));
}

/** 계정별 합계 한 줄(#406 — 인사팀은 차주별 소계보다 계정별 전체 합계를 본다). */
export type AccountTotal = { account: string; n: number; owners: number; plus: number; minus: number };

/** 지금 걸린 결과(검색·거르기 · 쪽과 무관) 전부를 계정별로 묶어 건수·차주 수·지급·공제 합. 금액 큰 순. */
export function accountTotals(groups: MoGroup[]): AccountTotal[] {
  const m = new Map<string, { n: number; owners: Set<string>; plus: number; minus: number }>();
  for (const g of groups) {
    for (const r of g.rows) {
      const k = r.account_name ?? '';
      let t = m.get(k);
      if (!t) { t = { n: 0, owners: new Set(), plus: 0, minus: 0 }; m.set(k, t); }
      t.n += 1;
      t.owners.add(String(g.owner_id ?? g.owner_name ?? r.owner_id ?? ''));
      const a = Number(r.amt ?? 0);
      if (r.is_plus) t.plus += a; else t.minus += a;
    }
  }
  return [...m.entries()]
    .map(([account, t]) => ({ account, n: t.n, owners: t.owners.size, plus: t.plus, minus: t.minus }))
    .sort((a, b) => (b.plus + b.minus) - (a.plus + a.minus) || a.account.localeCompare(b.account, 'ko'));
}
